import { errorText } from '@papan/api-client'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link2, Folder, File as FileIcon, X, ArrowUpDown, Mail, Globe, Search, ChevronDown } from 'lucide-react'
import { useToast, useConfirm, Tooltip } from '@papan/core-ui/ui'
import { disambiguateParentLabels } from '@papan/types'
import { listManageableShareLinks, revokeShareLink, ownerRevokeShareLink, type ManageableShareLink, type ShareLinkWithUrl } from '../client/index.js'
import { LinkRow, avatarBg, initialFromEmail, nameFromEmail, linkAudience, AUDIENCE_LABEL, type LinkAudience } from './ShareLinksDialog.js'

type StatusFilter = 'active' | 'expired' | 'revoked' | 'empty'
type PermissionFilter = 'edit' | 'app' | 'view-live' | 'view-save' | 'blocked'
/** Same three audiences the row chip renders — see `linkAudience`. This used
 *  to be a two-way public/not-public split whose "restricted" swept up Private
 *  links too, so filtering on Restricted returned rows badged Private. */
type AudienceFilter = LinkAudience
type SortKey = 'newest' | 'oldest' | 'views' | 'expiring' | 'recipients'

const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'views', label: 'Most views' },
  { value: 'expiring', label: 'Expiring soon' },
  { value: 'recipients', label: 'Most recipients' },
]

const STATUS_LABELS: Record<StatusFilter, string> = {
  active: 'Active',
  expired: 'Expired',
  revoked: 'Revoked',
  empty: 'No recipients',
}

const PERMISSION_LABELS: Record<PermissionFilter, string> = {
  edit: 'Edit',
  app: 'App',
  'view-live': 'View (Live Edit)',
  'view-save': 'View (Last Saved)',
  blocked: 'Blocked',
}

/** Returns the set of permission buckets a single link belongs to.
 *  Same semantics as the WhoHasAccess recipient buckets but applied at
 *  the link level: a link matches a bucket if it has at least one
 *  contributing grant for that category. App-kind links always live in
 *  the App bucket (view-only by design); block grants live in the
 *  Blocked bucket regardless of kind. */
function permissionBucketsForLink(link: ManageableShareLink): Set<PermissionFilter> {
  const out = new Set<PermissionFilter>()
  if (link.kind === 'app') out.add('app')
  for (const grant of Object.values(link.recipientGrants)) {
    if (grant.permission === 'block') {
      out.add('blocked')
      continue
    }
    if (link.kind === 'app') continue // App is view-only — counted only in 'app'
    if (grant.permission === 'edit') out.add('edit')
    else if (grant.liveMode) out.add('view-live')
    else out.add('view-save')
  }
  return out
}

/** Ordered closed → open, matching the editor's own ordering. */
const AUDIENCE_FILTERS: AudienceFilter[] = ['private', 'restricted', 'public']

/**
 * Tenant-wide link manager surfaced under Settings → Links. Mirrors the
 * Teams page layout: left nav with `All Links` synthetic entry +
 * per-root-file groupings; right pane shows a filter toolbar, sort
 * dropdown, and a list of LinkRows the caller can manage (same
 * component used in the share-links dialog so edit / revoke / send
 * stay consistent).
 */

/** The sidebar group for a row with no `rootFile`: a deleted source, or one
 *  that exists but whose location this viewer may not see (`rootWithheld`). */
function unlabelledGroup(link: ManageableShareLink): { id: string; name: string } {
  return link.rootWithheld
    ? { id: '__withheld__', name: 'Location hidden (no access)' }
    : { id: '__detached__', name: 'Detached (source deleted)' }
}

export function LinksPage() {
  const toast = useToast()
  const confirm = useConfirm()

  const [links, setLinks] = useState<ManageableShareLink[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Left-panel tab + selection per tab. Each tab has an "All" entry
  // (selection = null) and a list of grouped entries below.
  const [panelTab, setPanelTab] = useState<'links' | 'emails'>('links')
  const [selectedRootId, setSelectedRootId] = useState<string | null>(null)
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null)
  // Free-text search box at the top of the left panel. Filters the
  // current tab's list (file names on Links, email/name on Emails).
  const [sidebarQuery, setSidebarQuery] = useState('')
  const [statusFilters, setStatusFilters] = useState<Set<StatusFilter>>(() => new Set(['active']))
  const [permissionFilters, setPermissionFilters] = useState<Set<PermissionFilter>>(new Set())
  const [audienceFilters, setAudienceFilters] = useState<Set<AudienceFilter>>(new Set())
  // #348 (owner, 2026-10-05): default links are hidden unless asked for. One is created just by
  // opening the Share dialog, grants nothing of its own (each visitor gets only the access they
  // already hold) and can't be edited or revoked — noise among the links someone made.
  const [showDefaultLinks, setShowDefaultLinksState] = useState(false)
  // Hiding them again can empty the selected file's group (it held only default links):
  // go back to All Links rather than an empty, unexplained list.
  const setShowDefaultLinks = useCallback((next: boolean) => {
    setShowDefaultLinksState(next)
    if (!next) {
      setSelectedRootId((sel) => {
        if (sel === null) return sel
        const keeps = links.some((l) => !l.isDefault && (l.rootFile?.id ?? unlabelledGroup(l).id) === sel)
        return keeps ? sel : null
      })
    }
  }, [links])
  const defaultLinksHidden = !showDefaultLinks && links.some((l) => l.isDefault)
  const [sortKey, setSortKey] = useState<SortKey>('newest')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listManageableShareLinks()
      .then((rows) => { if (!cancelled) setLinks(rows) })
      .catch((e: unknown) => {
        if (cancelled) return
        // #1316: dropped at a session end — Settings remounts with the session, which reloads the page
        const text = errorText(e, 'Failed to load links')
        if (text !== null) setError(text)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  // Single source of truth: links that pass the *toolbar* filters
  // (status / permission / access). Sidebar counts derive from this
  // so the badges answer "if I click this, what's left?" — consistent
  // with the right pane's behaviour. The sidebar selection itself is
  // applied on top of this in `visible`.
  //
  // We floor the "now" reference to the start of the current minute
  // and re-tick once a minute, so `linksMatchingToolbar` (and the
  // downstream memos) only invalidate when an expiry actually crosses
  // a boundary — not on every render / keystroke.
  const [nowMemo, setNowMemo] = useState(() => Math.floor(Date.now() / 60_000) * 60_000)
  useEffect(() => {
    const id = setInterval(() => {
      setNowMemo(Math.floor(Date.now() / 60_000) * 60_000)
    }, 60_000)
    return () => clearInterval(id)
  }, [])
  const linksMatchingToolbar = useMemo(() => {
    return links.filter((l) => {
      if (l.isDefault && !showDefaultLinks) return false
      if (statusFilters.size > 0) {
        const statuses = computeStatuses(l, nowMemo)
        let any = false
        for (const s of statusFilters) if (statuses.has(s)) { any = true; break }
        if (!any) return false
      }
      if (permissionFilters.size > 0) {
        const buckets = permissionBucketsForLink(l)
        let any = false
        for (const f of permissionFilters) if (buckets.has(f)) { any = true; break }
        if (!any) return false
      }
      if (audienceFilters.size > 0 && !audienceFilters.has(linkAudience(l.recipientGrants, l.strictAudience))) return false
      return true
    })
  }, [links, statusFilters, permissionFilters, audienceFilters, showDefaultLinks, nowMemo])

  // Group toolbar-matched rows by rootFileId. Each group keeps the
  // root-file metadata for the sidebar label; null-rootFile rows
  // (file deleted) get collapsed under a synthetic "Detached" bucket.
  const roots = useMemo(() => {
    type Root = { id: string; name: string; isFolder: boolean; count: number; path: string; parentName: string }
    const map = new Map<string, Root>()
    for (const link of linksMatchingToolbar) {
      const root = link.rootFile
      const id = root?.id ?? unlabelledGroup(link).id
      const name = root?.name ?? unlabelledGroup(link).name
      const path = root?.path ?? name
      const existing = map.get(id)
      if (existing) {
        existing.count++
      } else {
        // Server-authoritative — the /managed CTE pins leaf_is_folder
        // off files.is_folder so the sidebar icon never disagrees with
        // the source row. `parentName` is filled below via the shared
        // disambiguator once the whole set is known.
        map.set(id, {
          id,
          name,
          isFolder: link.rootFile?.isFolder ?? false,
          count: 1,
          path,
          parentName: '',
        })
      }
    }
    // VS-Code-style disambiguator (shared helper): the shortest parent-path
    // suffix that tells same-named source files apart. Paths are "root / a / b
    // / file", so pass the " / " separator.
    const rootsArr = Array.from(map.values())
    const labels = disambiguateParentLabels(rootsArr.map(r => r.path), ' / ')
    rootsArr.forEach((r, i) => { r.parentName = labels[i]! })
    return rootsArr.sort((a, b) => a.name.localeCompare(b.name))
  }, [linksMatchingToolbar])

  // Sidebar lists filtered by the search box. Case-insensitive substring
  // match against the entry's display label (file name on Links;
  // name + email on Emails).
  const sidebarQueryNorm = sidebarQuery.trim().toLowerCase()
  const filteredRoots = useMemo(() => {
    if (!sidebarQueryNorm) return roots
    return roots.filter((r) =>
      r.name.toLowerCase().includes(sidebarQueryNorm) ||
      r.path.toLowerCase().includes(sidebarQueryNorm),
    )
  }, [roots, sidebarQueryNorm])

  // Aggregate links by recipient email so the Emails tab can group +
  // count without duplicating the data shape. The '*' wildcard is
  // surfaced as a synthetic "Anyone with the link" entry that lives
  // above the email list. Block grants are ignored — a blocked
  // recipient hasn't been granted anything to count.
  const emails = useMemo(() => {
    const map = new Map<string, number>()
    let anyoneCount = 0
    for (const link of linksMatchingToolbar) {
      for (const [key, grant] of Object.entries(link.recipientGrants)) {
        if (grant.permission === 'block') continue
        if (key === '*') {
          anyoneCount++
          continue
        }
        map.set(key, (map.get(key) ?? 0) + 1)
      }
    }
    return {
      anyoneCount,
      emails: Array.from(map.entries())
        .map(([email, count]) => ({ email, count }))
        .sort((a, b) => a.email.localeCompare(b.email)),
    }
  }, [linksMatchingToolbar])

  const filteredEmails = useMemo(() => {
    if (!sidebarQueryNorm) return emails.emails
    return emails.emails.filter(({ email }) => {
      const hay = `${nameFromEmail(email)} ${email}`.toLowerCase()
      return hay.includes(sidebarQueryNorm)
    })
  }, [emails.emails, sidebarQueryNorm])

  // Apply tab-scoped selection + sort on top of the already-toolbar-
  // filtered set (`linksMatchingToolbar`). Each tab narrows the
  // visible set with a different lens:
  //   - links tab + selectedRootId: links rooted at that file
  //   - emails tab + '*':        links with the wildcard recipient
  //   - emails tab + an email:   links granting that email (non-block)
  //   - emails tab + null:       all links with at least one recipient
  const visible = useMemo(() => {
    let out = linksMatchingToolbar
    if (panelTab === 'links' && selectedRootId) {
      out = out.filter((l) => (l.rootFile?.id ?? unlabelledGroup(l).id) === selectedRootId)
    }
    if (panelTab === 'emails') {
      if (selectedEmail === '*') {
        out = out.filter((l) => '*' in l.recipientGrants)
      } else if (selectedEmail) {
        out = out.filter((l) => {
          const grant = l.recipientGrants[selectedEmail]
          return grant !== undefined && grant.permission !== 'block'
        })
      } else {
        out = out.filter((l) => Object.entries(l.recipientGrants)
          .some(([, g]) => g.permission !== 'block'))
      }
    }
    const sorted = [...out]
    switch (sortKey) {
      case 'newest':
        sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        break
      case 'oldest':
        sorted.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        break
      case 'views':
        sorted.sort((a, b) => b.viewCount - a.viewCount)
        break
      case 'expiring':
        sorted.sort((a, b) => {
          const av = a.expiresAt ? new Date(a.expiresAt).getTime() : Infinity
          const bv = b.expiresAt ? new Date(b.expiresAt).getTime() : Infinity
          return av - bv
        })
        break
      case 'recipients':
        sorted.sort((a, b) => recipientCount(b) - recipientCount(a))
        break
    }
    return sorted
  }, [linksMatchingToolbar, panelTab, selectedRootId, selectedEmail, sortKey])

  // Local cache updates so edit/revoke don't need to refetch the
  // entire list. revokeShareLink succeeded → drop the row.
  const handleLinkPatched = useCallback((updated: ShareLinkWithUrl) => {
    setLinks((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated, rootFile: l.rootFile } : l)))
  }, [])
  const handleRevoke = useCallback(async (link: ManageableShareLink) => {
    const ok = await confirm({
      title: 'Revoke this link?',
      message: (
        <span>
          <code>{link.url}</code> will stop working immediately. The token is
          permanently burned — even a future link on the same file gets a fresh,
          unrelated URL. This can't be undone.
        </span>
      ),
      type: 'danger',
      confirmText: 'Revoke',
    })
    if (!ok) return
    setBusyId(link.id)
    try {
      // Inbound reshares live in the resharer's tenant — revoke them through the
      // owner-authority endpoint, not the creator path (which would 403).
      if (link.reshareFrom) await ownerRevokeShareLink(link.id)
      else await revokeShareLink(link.id)
      setLinks((prev) => prev.filter((l) => l.id !== link.id))
      setEditingId((cur) => (cur === link.id ? null : cur))
      toast.success('Link revoked')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to revoke')
    } finally {
      setBusyId(null)
    }
  }, [confirm, toast])
  const handleCopy = useCallback((url: string) => {
    void navigator.clipboard.writeText(url)
      .then(() => toast.success('Link copied to clipboard'))
      .catch(() => toast.info(`Link: ${url}`))
  }, [toast])

  return (
    <div>
      <h2 className="text-lg font-medium themed-text mb-1">Links</h2>
      <p className="text-xs themed-text-muted mb-4">
        Every share link you can manage in one place — links you created, plus
        links on folders where you're an admin. Use the filters and sort to
        prune the list; click a row to edit, revoke, or copy the URL.
      </p>

      <div className="flex gap-4" style={{ minHeight: 320 }}>
        {/* ── Left sidebar: tab switcher + list ────────────────────────── */}
        <div className="w-52 shrink-0 themed-bg-secondary rounded-lg flex flex-col overflow-hidden">
          <div className="flex border-b themed-border shrink-0">
            <button
              type="button"
              onClick={() => setPanelTab('links')}
              className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                panelTab === 'links'
                  ? 'border-blue-500 text-blue-300'
                  : 'border-transparent themed-text-muted hover:themed-text'
              }`}
            >
              Links
            </button>
            <button
              type="button"
              onClick={() => setPanelTab('emails')}
              className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                panelTab === 'emails'
                  ? 'border-blue-500 text-blue-300'
                  : 'border-transparent themed-text-muted hover:themed-text'
              }`}
            >
              Emails
            </button>
          </div>
          {loading ? (
            <div className="p-3 flex items-center gap-2 themed-text-muted text-sm">
              Loading…
            </div>
          ) : (
            <>
              {/* Search box. Filters file names on Links and
                  name + email on Emails. */}
              <div className="px-2 py-2 shrink-0">
                <div className="relative">
                  <Search
                    size={12}
                    className="absolute left-2 top-1/2 -translate-y-1/2 themed-text-muted pointer-events-none"
                  />
                  <input
                    type="text"
                    value={sidebarQuery}
                    onChange={(e) => setSidebarQuery(e.target.value)}
                    placeholder={panelTab === 'links' ? 'Search files…' : 'Search recipients…'}
                    className="w-full themed-bg-primary themed-text rounded pl-7 pr-6 py-1 text-xs outline-none focus:ring-1 focus:ring-blue-500/60 placeholder:themed-text-muted"
                  />
                  {sidebarQuery && (
                    <button
                      type="button"
                      onClick={() => setSidebarQuery('')}
                      className="absolute right-1 top-1/2 -translate-y-1/2 themed-text-muted hover:themed-text p-0.5 rounded"
                      aria-label="Clear search"
                    >
                      <X size={11} />
                    </button>
                  )}
                </div>
              </div>
              {panelTab === 'links' ? (
                <div className="overflow-y-auto flex flex-col">
                  <button
                    type="button"
                    onClick={() => setSelectedRootId(null)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm transition-colors ${
                      selectedRootId === null
                        ? 'bg-blue-600/20 text-blue-300'
                        : 'themed-text hover:themed-bg-tertiary'
                    }`}
                  >
                    <Link2 size={14} className="shrink-0" />
                    <span className="flex-1 text-left truncate">All Links</span>
                    <span className="text-[11px] tabular-nums themed-text-muted">{linksMatchingToolbar.length}</span>
                  </button>
                  {filteredRoots.length === 0 ? (
                    <div className="p-3 text-xs themed-text-muted">
                      {sidebarQuery
                        ? 'No source files match this search.'
                        : defaultLinksHidden && !links.some((l) => !l.isDefault)
                          ? 'Only default links here — turn on "Show default links" to see them.'
                          : 'No links you can manage. Create one from the share menu on any file.'}
                    </div>
                  ) : (
                    filteredRoots.map((root) => {
                      const active = selectedRootId === root.id
                      return (
                        <Tooltip
                          key={root.id}
                          content={root.path}
                          always
                          followCursor
                          className="block"
                        >
                          <button
                            type="button"
                            onClick={() => setSelectedRootId(root.id)}
                            className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
                              active
                                ? 'bg-blue-600/20 text-blue-300'
                                : 'themed-text hover:themed-bg-tertiary'
                            }`}
                          >
                            {root.isFolder ? (
                              <Folder size={14} className="text-yellow-500 shrink-0" />
                            ) : (
                              <FileIcon size={14} className="themed-text-muted shrink-0" />
                            )}
                            <div className="flex-1 min-w-0 text-left">
                              <div className="truncate text-[12px] leading-tight">{root.name}</div>
                              {root.parentName && (
                                <div className="truncate text-[10px] themed-text-muted leading-tight">
                                  {root.parentName}
                                </div>
                              )}
                            </div>
                            <span className="text-[11px] tabular-nums themed-text-muted shrink-0">{root.count}</span>
                          </button>
                        </Tooltip>
                      )
                    })
                  )}
                </div>
              ) : (
                <div className="overflow-y-auto flex flex-col">
                  {emails.anyoneCount > 0 && (!sidebarQueryNorm || 'anyone with the link'.includes(sidebarQueryNorm)) && (
                    <button
                      type="button"
                      onClick={() => setSelectedEmail('*')}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm transition-colors ${
                        selectedEmail === '*'
                          ? 'bg-blue-600/20 text-blue-300'
                          : 'themed-text hover:themed-bg-tertiary'
                      }`}
                    >
                      <Globe size={14} className="themed-text-muted shrink-0" />
                      <span className="flex-1 text-left truncate">Anyone with the link</span>
                      <span className="text-[11px] tabular-nums themed-text-muted">{emails.anyoneCount}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setSelectedEmail(null)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm transition-colors ${
                      selectedEmail === null
                        ? 'bg-blue-600/20 text-blue-300'
                        : 'themed-text hover:themed-bg-tertiary'
                    }`}
                  >
                    <Mail size={14} className="shrink-0" />
                    <span className="flex-1 text-left truncate">All Emails</span>
                    <span className="text-[11px] tabular-nums themed-text-muted">{emails.emails.length}</span>
                  </button>
                  {filteredEmails.length === 0 && emails.anyoneCount === 0 ? (
                    <div className="p-3 text-xs themed-text-muted">
                      {sidebarQuery
                        ? 'No recipients match this search.'
                        : 'No specific email recipients across your links yet.'}
                    </div>
                  ) : filteredEmails.length === 0 ? (
                    <div className="p-3 text-xs themed-text-muted">No recipients match this search.</div>
                  ) : (
                    filteredEmails.map(({ email, count }) => {
                      const active = selectedEmail === email
                      const displayName = nameFromEmail(email)
                      return (
                        <button
                          key={email}
                          type="button"
                          onClick={() => setSelectedEmail(email)}
                          title={email}
                          className={`flex items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
                            active
                              ? 'bg-blue-600/20 text-blue-300'
                              : 'themed-text hover:themed-bg-tertiary'
                          }`}
                        >
                          <div className={`w-6 h-6 rounded-full ${avatarBg(email)} flex items-center justify-center text-white text-[9px] font-semibold shrink-0 select-none`}>
                            {initialFromEmail(email)}
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <div className="truncate text-[12px] leading-tight">{displayName}</div>
                            <div className="truncate text-[10px] themed-text-muted leading-tight">{email}</div>
                          </div>
                          <span className="text-[11px] tabular-nums themed-text-muted shrink-0">{count}</span>
                        </button>
                      )
                    })
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Right pane: selection header + toolbar + list ──────────── */}
        <div className="flex-1 min-w-0 themed-bg-secondary rounded-lg flex flex-col overflow-hidden">
          {loading ? (
            <div className="p-6 text-sm themed-text-muted">Loading links…</div>
          ) : error ? (
            <div className="p-6 text-sm text-red-400">{error}</div>
          ) : (
            <>
              <SelectionHeader
                panelTab={panelTab}
                selectedRoot={selectedRootId === null ? null : roots.find((r) => r.id === selectedRootId) ?? null}
                selectedEmail={selectedEmail}
                wildcardCount={emails.anyoneCount}
                emailCount={emails.emails.find((e) => e.email === selectedEmail)?.count}
              />
              <Toolbar
                statusFilters={statusFilters}
                setStatusFilters={setStatusFilters}
                permissionFilters={permissionFilters}
                setPermissionFilters={setPermissionFilters}
                audienceFilters={audienceFilters}
                setAudienceFilters={setAudienceFilters}
                showDefaultLinks={showDefaultLinks}
                setShowDefaultLinks={setShowDefaultLinks}
                sortKey={sortKey}
                setSortKey={setSortKey}
                totalMatched={visible.length}
              />
              <div className="flex-1 min-h-0 overflow-y-auto p-4">
                {visible.length === 0 ? (
                  <p className="text-sm themed-text-muted">No links match these filters.</p>
                ) : panelTab === 'links' && selectedRootId === null ? (
                  // "All Links" view — group by source file so each
                  // row is anchored to a header (folder / file name +
                  // muted parent folder). Order matches the sidebar's
                  // alphabetical sort.
                  <SourceGroupedList
                    links={visible}
                    editingId={editingId}
                    busyId={busyId}
                    onStartEdit={setEditingId}
                    onCancelEdit={() => setEditingId(null)}
                    onCopy={handleCopy}
                    onRevoke={handleRevoke}
                    onLinkPatched={handleLinkPatched}
                  />
                ) : (
                  <div className="space-y-2">
                    {visible.map((link) => (
                      <div key={link.id}>
                      {link.isDefault && <DefaultLinkNote />}
                      <LinkRow
                        link={link}
                        isFolder={link.rootFile?.isFolder ?? false}
                        canGrantEdit={!link.reshareFrom}
                        creatorShareOverlay={!!link.reshareFrom}
                        creatorIsOrg={link.reshareFrom?.isOrg ?? false}
                        creatorOrgName={link.reshareFrom?.tenantName}
                        isEditing={editingId === link.id}
                        isBusy={busyId === link.id}
                        onStartEdit={() => setEditingId(link.id)}
                        onCancelEdit={() => setEditingId(null)}
                        onClose={() => setEditingId(null)}
                        onCopy={() => handleCopy(link.url)}
                        onRevoke={() => handleRevoke(link)}
                        onLinkPatched={handleLinkPatched}
                      />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/** Determine which status buckets a link belongs to. A link can match
 *  multiple (e.g. expired AND empty), so the filter is OR within the
 *  category. */
function computeStatuses(link: ManageableShareLink, now: number): Set<StatusFilter> {
  const out = new Set<StatusFilter>()
  if (link.isRevoked) out.add('revoked')
  else {
    const expired = link.expiresAt && new Date(link.expiresAt).getTime() < now
    if (expired) out.add('expired')
    else out.add('active')
  }
  if (Object.keys(link.recipientGrants).length === 0) out.add('empty')
  return out
}

function recipientCount(link: ManageableShareLink): number {
  return Object.keys(link.recipientGrants).length
}

function Toolbar({
  statusFilters,
  setStatusFilters,
  permissionFilters,
  setPermissionFilters,
  audienceFilters,
  setAudienceFilters,
  showDefaultLinks,
  setShowDefaultLinks,
  sortKey,
  setSortKey,
  totalMatched,
}: {
  statusFilters: Set<StatusFilter>
  setStatusFilters: (next: Set<StatusFilter>) => void
  permissionFilters: Set<PermissionFilter>
  setPermissionFilters: (next: Set<PermissionFilter>) => void
  audienceFilters: Set<AudienceFilter>
  setAudienceFilters: (next: Set<AudienceFilter>) => void
  showDefaultLinks: boolean
  setShowDefaultLinks: (next: boolean) => void
  sortKey: SortKey
  setSortKey: (next: SortKey) => void
  totalMatched: number
}) {
  const toggle = <T,>(set: Set<T>, value: T, setter: (next: Set<T>) => void) => {
    const next = new Set(set)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    setter(next)
  }
  const totalActiveFilters = statusFilters.size + permissionFilters.size + audienceFilters.size + (showDefaultLinks ? 1 : 0)
  const clearAll = () => {
    setStatusFilters(new Set())
    setPermissionFilters(new Set())
    setAudienceFilters(new Set())
    setShowDefaultLinks(false)
  }
  return (
    <div className="themed-bg-sunken-secondary border-b themed-border px-4 py-3 flex flex-wrap items-center gap-2">
      <FilterGroup label="Status">
        {(Object.keys(STATUS_LABELS) as StatusFilter[]).map((s) => (
          <FilterChip
            key={s}
            label={STATUS_LABELS[s]}
            active={statusFilters.has(s)}
            onClick={() => toggle(statusFilters, s, setStatusFilters)}
          />
        ))}
      </FilterGroup>
      <FilterGroup label="Permission">
        {(Object.keys(PERMISSION_LABELS) as PermissionFilter[]).map((p) => (
          <FilterChip
            key={p}
            label={PERMISSION_LABELS[p]}
            active={permissionFilters.has(p)}
            onClick={() => toggle(permissionFilters, p, setPermissionFilters)}
          />
        ))}
      </FilterGroup>
      <FilterGroup label="Access">
        {AUDIENCE_FILTERS.map((a) => (
          <FilterChip
            key={a}
            label={AUDIENCE_LABEL[a]}
            active={audienceFilters.has(a)}
            onClick={() => toggle(audienceFilters, a, setAudienceFilters)}
          />
        ))}
      </FilterGroup>
      <FilterChip
        label="Show default links"
        active={showDefaultLinks}
        onClick={() => setShowDefaultLinks(!showDefaultLinks)}
      />
      {totalActiveFilters > 0 && (
        <button
          type="button"
          onClick={clearAll}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
        >
          <X size={11} /> Clear filters
        </button>
      )}
      <span className="flex-1" />
      <span className="text-[11px] themed-text-muted">
        {totalMatched} {totalMatched === 1 ? 'link' : 'links'}
      </span>
      <label className="inline-flex items-center gap-1.5 text-[11px] themed-text-muted">
        <ArrowUpDown size={11} />
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as SortKey)}
          className="themed-bg-primary themed-text border themed-border rounded px-2 py-1 text-[11px] outline-none focus:border-blue-500"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </label>
    </div>
  )
}

/**
 * Right-pane list grouped by source file/folder, with a small header
 * row above each group. Used only when "All Links" is selected so
 * the user can see which file each link belongs to without scanning
 * the LinkRow chip.
 */
function SourceGroupedList({
  links,
  editingId,
  busyId,
  onStartEdit,
  onCancelEdit,
  onCopy,
  onRevoke,
  onLinkPatched,
}: {
  links: ManageableShareLink[]
  editingId: string | null
  busyId: string | null
  onStartEdit: (id: string) => void
  onCancelEdit: () => void
  onCopy: (url: string) => void
  onRevoke: (link: ManageableShareLink) => void
  onLinkPatched: (updated: ShareLinkWithUrl) => void
}) {
  // Group preserving the order in which sources first appear in
  // `links` — that lets `sortKey` on the parent flow through (e.g.
  // sort by Newest puts the most-recent source's group first).
  const groups: Array<{
    key: string
    name: string
    path: string
    parentName: string
    isFolder: boolean
    links: ManageableShareLink[]
  }> = []
  const indexByKey = new Map<string, number>()
  for (const link of links) {
    const id = link.rootFile?.id ?? unlabelledGroup(link).id
    const idx = indexByKey.get(id)
    if (idx !== undefined) {
      groups[idx]!.links.push(link)
    } else {
      const name = link.rootFile?.name ?? unlabelledGroup(link).name
      const path = link.rootFile?.path ?? name
      const segments = path.split(' / ')
      const parentName = segments.length > 1 ? segments[segments.length - 2]! : ''
      indexByKey.set(id, groups.length)
      groups.push({
        key: id,
        name,
        path,
        parentName,
        isFolder: link.rootFile?.isFolder ?? false,
        links: [link],
      })
    }
  }
  // Collapsed groups (per source). Default-open; clicking the header
  // toggles the entry into the set. Editing a row force-opens its
  // source so the EditPanel doesn't get hidden mid-edit.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const toggle = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }
  return (
    <div className="space-y-4">
      {groups.map((g) => {
        const isOpen = !collapsed.has(g.key) || g.links.some((l) => l.id === editingId)
        return (
          <div key={g.key} className="space-y-2">
            <Tooltip content={g.path} always followCursor className="block">
              <button
                type="button"
                onClick={() => toggle(g.key)}
                className="w-full flex items-center gap-2 px-1 pb-1 themed-text hover:opacity-80 transition-opacity"
                aria-expanded={isOpen}
              >
                <ChevronDown
                  size={14}
                  className={`themed-text-muted shrink-0 transition-transform ${isOpen ? '' : '-rotate-90'}`}
                />
                {g.isFolder ? (
                  <Folder size={14} className="text-yellow-500 shrink-0" />
                ) : (
                  <FileIcon size={14} className="themed-text-muted shrink-0" />
                )}
                <span className="text-sm font-medium truncate">{g.name}</span>
                {g.parentName && (
                  <span className="text-[10px] themed-text-muted truncate">{g.parentName}</span>
                )}
                <span className="flex-1" />
                <span className="text-[11px] themed-text-muted tabular-nums shrink-0">
                  {g.links.length} {g.links.length === 1 ? 'link' : 'links'}
                </span>
              </button>
            </Tooltip>
            {isOpen && (
              <div className="space-y-2">
                {g.links.map((link) => (
                  <div key={link.id}>
                  {link.isDefault && <DefaultLinkNote />}
                  <LinkRow
                    link={link}
                    isFolder={g.isFolder}
                    canGrantEdit={!link.reshareFrom}
                    creatorShareOverlay={!!link.reshareFrom}
                    creatorIsOrg={link.reshareFrom?.isOrg ?? false}
                    creatorOrgName={link.reshareFrom?.tenantName}
                    isEditing={editingId === link.id}
                    isBusy={busyId === link.id}
                    onStartEdit={() => onStartEdit(link.id)}
                    onCancelEdit={onCancelEdit}
                    onClose={onCancelEdit}
                    onCopy={() => onCopy(link.url)}
                    onRevoke={() => onRevoke(link)}
                    onLinkPatched={onLinkPatched}
                  />
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 w-full">
      <span className="text-[10px] uppercase tracking-wider themed-text-muted font-semibold pt-1 shrink-0">{label}</span>
      <div className="flex flex-wrap items-center gap-1 flex-1 min-w-0">
        {children}
      </div>
    </div>
  )
}

/** What a default link is, above its row (#348): shown only with "Show default links" on. */
function DefaultLinkNote() {
  return (
    <p className="text-[11px] themed-text-muted mb-1">
      Default link — follows each person's own access; it can't be edited or revoked.
    </p>
  )
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`px-2 py-0.5 text-[11px] rounded-full transition-colors whitespace-nowrap shrink-0 border ${
        active
          ? 'bg-purple-500/30 text-purple-200 font-medium border-purple-500/40'
          : 'themed-bg-primary themed-text-muted hover:themed-bg-tertiary themed-border'
      }`}
    >
      {label}
    </button>
  )
}

/**
 * Right-pane selection header. Echoes whatever the sidebar is showing
 * so the user always has confirmation of the active filter scope —
 * full file path on the Links tab, name + email on Emails.
 */
function SelectionHeader({
  panelTab,
  selectedRoot,
  selectedEmail,
  wildcardCount,
  emailCount,
}: {
  panelTab: 'links' | 'emails'
  selectedRoot: { id: string; name: string; isFolder: boolean; count: number; path: string; parentName: string } | null
  selectedEmail: string | null
  wildcardCount: number
  emailCount: number | undefined
}) {
  if (panelTab === 'links') {
    if (!selectedRoot) {
      return <Header icon={<Link2 size={14} className="themed-text-muted" />} title="All Links" subtitle="Every link you can manage" />
    }
    return (
      <Header
        icon={selectedRoot.isFolder
          ? <Folder size={14} className="text-yellow-500" />
          : <FileIcon size={14} className="themed-text-muted" />}
        title={selectedRoot.name}
        subtitle={selectedRoot.path}
        countSuffix={`${selectedRoot.count} ${selectedRoot.count === 1 ? 'link' : 'links'}`}
      />
    )
  }
  if (selectedEmail === '*') {
    return (
      <Header
        icon={<Globe size={14} className="themed-text-muted" />}
        title="Anyone with the link"
        subtitle="Public links — anyone holding the URL can open"
        countSuffix={`${wildcardCount} ${wildcardCount === 1 ? 'link' : 'links'}`}
      />
    )
  }
  if (selectedEmail) {
    const displayName = nameFromEmail(selectedEmail)
    return (
      <Header
        icon={
          <div className={`w-6 h-6 rounded-full ${avatarBg(selectedEmail)} flex items-center justify-center text-white text-[9px] font-semibold shrink-0 select-none`}>
            {initialFromEmail(selectedEmail)}
          </div>
        }
        title={displayName}
        subtitle={selectedEmail}
        countSuffix={emailCount !== undefined ? `${emailCount} ${emailCount === 1 ? 'link' : 'links'}` : undefined}
      />
    )
  }
  return <Header icon={<Mail size={14} className="themed-text-muted" />} title="All Emails" subtitle="Every recipient across your links" />
}

function Header({
  icon,
  title,
  subtitle,
  countSuffix,
}: {
  icon: React.ReactNode
  title: string
  subtitle?: string
  countSuffix?: string
}) {
  return (
    <div className="flex items-center gap-2.5 px-4 py-3 border-b themed-border shrink-0">
      <div className="shrink-0 flex items-center justify-center">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="themed-text text-sm font-medium truncate" title={title}>{title}</div>
        {subtitle && (
          <div className="themed-text-muted text-[11px] truncate" title={subtitle}>{subtitle}</div>
        )}
      </div>
      {countSuffix && (
        <span className="text-[11px] themed-text-muted shrink-0 tabular-nums">{countSuffix}</span>
      )}
    </div>
  )
}
